import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from 'lib/api';

function generateReceipt(payment, memberName) {
  const html = `<!DOCTYPE html>
<html>
<head>
  <title>Invoice ${payment?.invoiceNumber}</title>
  <style>
    body { font-family: Arial, sans-serif; max-width: 600px; margin: 40px auto; padding: 20px; color: #333; }
    .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 20px; }
    .header h1 { margin: 0; font-size: 24px; }
    .header p { margin: 4px 0; color: #666; }
    .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
    .row.total { font-weight: bold; font-size: 16px; border-top: 2px solid #333; border-bottom: none; margin-top: 8px; }
    .badge { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 12px; font-weight: bold; }
    .paid { background: #d1fae5; color: #065f46; }
    .pending { background: #fef3c7; color: #92400e; }
    .overdue { background: #fee2e2; color: #991b1b; }
    .partial { background: #dbeafe; color: #1e40af; }
    .footer { text-align: center; margin-top: 30px; color: #999; font-size: 12px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>🏋️ GymVendor</h1>
    <p>Official Payment Receipt</p>
  </div>
  <div class="row"><span>Invoice #</span><span>${payment?.invoiceNumber ?? 'N/A'}</span></div>
  <div class="row"><span>Member</span><span>${memberName ?? 'N/A'}</span></div>
  <div class="row"><span>Description</span><span>${payment?.description ?? 'Membership'}</span></div>
  <div class="row"><span>Date</span><span>${payment?.date ?? 'N/A'}</span></div>
  <div class="row"><span>Payment Method</span><span>${payment?.paymentMethod ?? 'N/A'}</span></div>
  <div class="row"><span>Amount Due</span><span>$${Number(payment?.amount ?? 0)?.toFixed(2)}</span></div>
  <div class="row"><span>Amount Paid</span><span>$${Number(payment?.paidAmount ?? 0)?.toFixed(2)}</span></div>
  <div class="row total"><span>Status</span><span><span class="badge ${payment?.status ?? 'pending'}">${(payment?.status ?? '')?.toUpperCase()}</span></span></div>
  <div class="footer">Thank you for your membership! · Generated ${new Date()?.toLocaleDateString()}</div>
</body>
</html>`;
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice-${payment?.invoiceNumber ?? 'receipt'}.html`;
  a?.click();
  URL.revokeObjectURL(url);
}

export default function InvoiceList({ memberId, memberName }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const fetchPayments = useCallback(async () => {
    if (!memberId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/payments', { params: { member_id: memberId, limit: 2000 } });
      const mapped = (data ?? [])?.map((p, idx) => ({
        id: p?.id,
        invoiceNumber: `INV-${new Date(p?.created_at)?.getFullYear()}-${String(idx + 1)?.padStart(3, '0')}`,
        date: p?.paid_date
          ? new Date(p?.paid_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : p?.due_date
          ? new Date(p?.due_date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : '—',
        amount: p?.amount,
        paidAmount: p?.paid_amount,
        status: p?.payment_status,
        paymentMethod: p?.payment_method,
        description: p?.description,
        paymentType: p?.payment_status === 'partial' ? 'Partial' : 'Full',
      }));
      setPayments(mapped);
    } catch (err) {
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const outstandingBalance = payments?.reduce((sum, p) => {
    if (p?.status === 'partial' || p?.status === 'pending' || p?.status === 'overdue') {
      return sum + Math.max(0, Number(p?.amount) - Number(p?.paidAmount));
    }
    return sum;
  }, 0);

  const handleDownload = (payment) => {
    setDownloading(payment?.id);
    generateReceipt(payment, memberName);
    setTimeout(() => setDownloading(null), 1000);
  };

  const statusConfig = {
    paid: { color: 'bg-[var(--color-success)]/10 text-[var(--color-success)]', icon: 'FileCheck', iconColor: 'var(--color-success)' },
    partial: { color: 'bg-blue-500/10 text-blue-600', icon: 'FileText', iconColor: '#3b82f6' },
    pending: { color: 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]', icon: 'FileClock', iconColor: 'var(--color-warning)' },
    overdue: { color: 'bg-[var(--color-error)]/10 text-[var(--color-error)]', icon: 'FileWarning', iconColor: 'var(--color-error)' },
  };

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Invoice History</h3>
        {!loading && <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{payments?.length} invoice{payments?.length !== 1 ? 's' : ''}</span>}
      </div>

      {/* Outstanding Balance */}
      {!loading && outstandingBalance > 0 && (
        <div className="flex items-center gap-3 p-3 mb-4 bg-[var(--color-error)]/5 border border-[var(--color-error)]/20 rounded-[var(--radius-sm)]">
          <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">Outstanding Balance</p>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Partial or pending payments</p>
          </div>
          <span className="text-base font-bold text-[var(--color-error)] font-[var(--font-heading)]">${outstandingBalance?.toFixed(2)}</span>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-2">
          {[1, 2, 3, 4]?.map(i => (
            <div key={i} className="h-16 bg-[var(--color-muted)] animate-pulse rounded-[var(--radius-sm)]" />
          ))}
        </div>
      ) : error ? (
        <div className="flex items-center justify-center gap-2 py-8">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" />
          <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{error}</p>
        </div>
      ) : !memberId ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8">
          <Icon name="FileX" size={28} color="var(--color-muted-foreground)" />
          <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No member record found</p>
        </div>
      ) : payments?.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8">
          <Icon name="FileX" size={28} color="var(--color-muted-foreground)" />
          <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No invoices found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                {['Invoice #', 'Date', 'Description', 'Type', 'Amount', 'Status', '']?.map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-[var(--color-muted-foreground)] font-[var(--font-caption)] pb-2 pr-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments?.map(inv => {
                const cfg = statusConfig?.[inv?.status] ?? statusConfig?.pending;
                return (
                  <tr key={inv?.id} className="border-b border-[var(--color-border)]/50 hover:bg-[var(--color-muted)]/30 transition-colors">
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2">
                        <Icon name={cfg?.icon} size={14} color={cfg?.iconColor} />
                        <span className="text-xs font-medium text-[var(--color-foreground)] font-[var(--font-caption)] whitespace-nowrap">{inv?.invoiceNumber}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] whitespace-nowrap">{inv?.date}</td>
                    <td className="py-3 pr-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] max-w-[120px] truncate">{inv?.description ?? '—'}</td>
                    <td className="py-3 pr-3">
                      <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{inv?.paymentType}</span>
                    </td>
                    <td className="py-3 pr-3">
                      <div>
                        <span className="text-sm font-bold text-[var(--color-foreground)] font-[var(--font-caption)]">${Number(inv?.amount)?.toFixed(2)}</span>
                        {inv?.status === 'partial' && (
                          <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Paid: ${Number(inv?.paidAmount)?.toFixed(2)}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium font-[var(--font-caption)] capitalize ${cfg?.color}`}>{inv?.status}</span>
                    </td>
                    <td className="py-3">
                      <button
                        onClick={() => handleDownload(inv)}
                        className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors"
                        title="Download Receipt"
                      >
                        {downloading === inv?.id ? (
                          <div className="w-3.5 h-3.5 border-2 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Icon name="Download" size={14} color="var(--color-muted-foreground)" />
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
